import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index: number) => {
    setActive((index + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const current = items[active];

  return (
    <section
      className="relative w-full overflow-hidden rounded-xl bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[440px] w-full md:h-[500px]">
        {items.map((movie, index) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={movie.backdrop || movie.cover}
              alt={`${movie.title}剧照`}
              title={`${movie.title} - 神马影院影视资源`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/10"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full flex-col justify-center px-6 md:px-12">
          <div key={current.id} className="max-w-2xl animate-fade-up">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-background-50 dark:text-foreground-950">
                焦点推荐
              </span>
              {current.genres.slice(0, 3).map((genre) => (
                <span key={genre} className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-100">
                  {genre}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-3xl font-bold text-background-50 md:text-5xl">{current.title}</h1>
            <p className="mt-3 flex flex-wrap items-center gap-3 text-sm text-background-200">
              <span className="flex items-center gap-1 font-semibold text-secondary-400">
                <i className="ri-star-fill"></i> {current.rating.toFixed(1)}
              </span>
              <span>{current.year}</span>
              <span className="h-1 w-1 rounded-full bg-background-400"></span>
              <span>{current.region}</span>
              <span className="h-1 w-1 rounded-full bg-background-400"></span>
              <span>{current.duration}</span>
            </p>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-300 md:text-base">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 dark:text-foreground-950"
              >
                <i className="ri-play-circle-line text-lg"></i> 查看详情
              </Link>
              <Link
                to={`/category/${current.type}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-apps-2-line text-lg"></i> 更多同类
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => goTo(active - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(active + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-accent-500 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 gap-2">
          {items.map((movie, index) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`切换到${movie.title}`}
              className={`h-2 rounded-full transition-all ${
                index === active ? 'w-8 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}